import { useMsal } from '@azure/msal-react';
import { notification } from 'antd';
import { useState } from 'react';

import {
	LimitedEditionImportResultDto,
	LimitedEditionNumberDto,
	LimitedEditionReleasePreviewDto,
	LimitedEditionSearchParams,
	LimitedEditionWriteDto,
} from '../../api-client';
import { LimitedEditionApiClient } from '../../providers/api-provider';
import { saveBlob, today } from '../../utils/save-blob';

export const useLimitedEditionsAdmin = () => {
	const { instance } = useMsal();

	const [numbers, setNumbers] = useState<LimitedEditionNumberDto[]>([]);
	const [numbersTotal, setNumbersTotal] = useState<number>(0);
	const [numbersLoading, setNumbersLoading] = useState(false);

	const [savingNumber, setSavingNumber] = useState(false);
	const [removingNumber, setRemovingNumber] = useState(false);
	const [releasingNumber, setReleasingNumber] = useState(false);
	const [blockingNumber, setBlockingNumber] = useState(false);
	const [assigningNumber, setAssigningNumber] = useState(false);
	const [exportingNumbers, setExportingNumbers] = useState(false);
	const [importingNumbers, setImportingNumbers] = useState(false);
	const [releasingBySapCode, setReleasingBySapCode] = useState(false);

	const [importResult, setImportResult] =
		useState<LimitedEditionImportResultDto | null>(null);

	const handleNumbers = async (params: LimitedEditionSearchParams = {}) => {
		setNumbersLoading(true);
		try {
			const limitedEditionApi = await LimitedEditionApiClient(instance);
			const response = await limitedEditionApi.searchLimitedEditions(params);

			setNumbers(response.data?.items ?? []);
			setNumbersTotal(response.data?.totalCount ?? 0);
		} catch {
			notification.error({
				message: 'Unable to load the limited edition numbers!',
				description: 'Unable to load the limited edition numbers.',
				duration: 2,
			});

			setNumbers([]);
			setNumbersTotal(0);
		} finally {
			setNumbersLoading(false);
		}
	};

	/**
	 * Creates or updates a number.
	 * @returns true when it was saved, so the caller can close its form.
	 */
	const saveNumber = async (
		number: LimitedEditionWriteDto,
		id?: number,
	): Promise<boolean> => {
		setSavingNumber(true);
		try {
			const limitedEditionApi = await LimitedEditionApiClient(instance);
			const response = id
				? await limitedEditionApi.updateLimitedEdition(id, number)
				: await limitedEditionApi.createLimitedEdition(number);

			if (response.data?.warning) {
				notification.warning({
					message: 'Number saved with a warning',
					description: response.data.warning,
					duration: 6,
				});
			} else {
				notification.success({
					message: id ? 'Number updated!' : 'Number created!',
					description: `Number ${number.number} of sap code ${number.sapCode} saved.`,
					duration: 2,
				});
			}

			return true;
		} catch (reason: any) {
			notification.error({
				message: 'Unable to save the number!',
				description:
					reason?.response?.data?.title ?? 'Unable to save the number.',
				duration: 4,
			});

			return false;
		} finally {
			setSavingNumber(false);
		}
	};

	const removeNumber = async (id: number) => {
		setRemovingNumber(true);
		try {
			const limitedEditionApi = await LimitedEditionApiClient(instance);
			await limitedEditionApi.deleteLimitedEdition(id);

			notification.success({
				message: 'Number deleted!',
				description: 'The number is no longer part of the list.',
				duration: 2,
			});
		} catch (reason: any) {
			// A number held by an order cannot be deleted: the backend says so and that is what to show.
			notification.error({
				message: 'Unable to delete the number!',
				description:
					reason?.response?.data?.title ?? 'Unable to delete the number.',
				duration: 4,
			});
		} finally {
			setRemovingNumber(false);
		}
	};

	const releaseNumber = async (id: number) => {
		setReleasingNumber(true);
		try {
			const limitedEditionApi = await LimitedEditionApiClient(instance);
			const response = await limitedEditionApi.releaseLimitedEdition(id);

			// The order that was holding it keeps the number written on its rows, so the warning is the
			// important part of this answer, not a detail.
			notification.warning({
				message: 'Number released!',
				description:
					response.data?.warning ??
					`Number ${response.data?.number?.number} is available again.`,
				duration: 6,
			});
		} catch (reason: any) {
			notification.error({
				message: 'Unable to release the number!',
				description:
					reason?.response?.data?.title ?? 'Unable to release the number.',
				duration: 4,
			});
		} finally {
			setReleasingNumber(false);
		}
	};

	/** Passing no reason puts the number back in the pool. */
	const blockNumber = async (id: number, reason?: string | null) => {
		setBlockingNumber(true);
		try {
			const limitedEditionApi = await LimitedEditionApiClient(instance);
			const response = await limitedEditionApi.blockLimitedEdition(id, reason);

			notification.success({
				message: reason ? 'Number blocked!' : 'Number unblocked!',
				description: reason
					? `Number ${response.data?.number?.number} is out of the pool: ${reason}`
					: `Number ${response.data?.number?.number} is selectable again.`,
				duration: 3,
			});
		} catch (reason_: any) {
			notification.error({
				message: 'Unable to block the number!',
				description:
					reason_?.response?.data?.title ?? 'Unable to block the number.',
				duration: 4,
			});
		} finally {
			setBlockingNumber(false);
		}
	};

	const assignNumber = async (
		id: number,
		orderId: number,
		rowId?: number | null,
	): Promise<boolean> => {
		setAssigningNumber(true);
		try {
			const limitedEditionApi = await LimitedEditionApiClient(instance);
			const response = await limitedEditionApi.assignLimitedEdition(
				id,
				orderId,
				rowId,
			);

			if (response.data?.warning) {
				notification.warning({
					message: 'Number assigned with a warning',
					description: response.data.warning,
					duration: 6,
				});
			} else {
				notification.success({
					message: 'Number assigned!',
					description: `Number ${response.data?.number?.number} now belongs to order ${orderId}.`,
					duration: 3,
				});
			}

			return true;
		} catch (reason: any) {
			notification.error({
				message: 'Unable to assign the number!',
				description:
					reason?.response?.data?.title ?? 'Unable to assign the number.',
				duration: 4,
			});

			return false;
		} finally {
			setAssigningNumber(false);
		}
	};

	/**
	 * Counts what a bulk release would touch, so the confirmation can show real numbers.
	 * @returns null when the preview could not be read: the caller must not offer the release blindly.
	 */
	const previewRelease = async (
		sapCode: string,
	): Promise<LimitedEditionReleasePreviewDto | null> => {
		try {
			const limitedEditionApi = await LimitedEditionApiClient(instance);
			const response = await limitedEditionApi.previewLimitedEditionRelease(sapCode);

			return response.data;
		} catch {
			notification.error({
				message: 'Unable to read the release preview!',
				description: `Unable to count the numbers of sap code ${sapCode}.`,
				duration: 4,
			});

			return null;
		}
	};

	const releaseBySapCode = async (
		sapCode: string,
		includeConfirmedOrders: boolean,
	): Promise<boolean> => {
		setReleasingBySapCode(true);
		try {
			const limitedEditionApi = await LimitedEditionApiClient(instance);
			const response = await limitedEditionApi.releaseLimitedEditionsBySapCode(
				sapCode,
				includeConfirmedOrders,
			);

			const result = response.data;

			if (result.releasedCount === 0) {
				notification.info({
					message: 'Nothing to release',
					description: `No number of sap code ${sapCode} was held by an order.`,
					duration: 3,
				});
			} else {
				// The orders keep the numbers on their rows, so this is not a plain success message.
				notification.warning({
					message: 'Numbers released!',
					description: `${result.releasedCount} numbers of sap code ${sapCode} are available again, taken from ${result.affectedOrdersCount} orders. Those orders still carry them on their rows and will be refused at confirmation.`,
					duration: 8,
				});
			}

			return true;
		} catch (reason: any) {
			notification.error({
				message: 'Unable to release the numbers!',
				description:
					reason?.response?.data?.title ?? 'Unable to release the numbers.',
				duration: 4,
			});

			return false;
		} finally {
			setReleasingBySapCode(false);
		}
	};

	const exportNumbers = async () => {
		setExportingNumbers(true);
		try {
			const limitedEditionApi = await LimitedEditionApiClient(instance);
			const response = await limitedEditionApi.exportLimitedEditions();
			saveBlob(
				response.data,
				(response as any)?.headers,
				`limited_editions_export_${today()}.xlsx`,
			);
		} catch {
			notification.error({
				message: 'Export failed',
				description: 'Unable to export the limited edition numbers.',
				duration: 2,
			});
		} finally {
			setExportingNumbers(false);
		}
	};

	const exportNumbersTemplate = async () => {
		setExportingNumbers(true);
		try {
			const limitedEditionApi = await LimitedEditionApiClient(instance);
			const response =
				await limitedEditionApi.exportLimitedEditionsImportTemplate();
			saveBlob(
				response.data,
				(response as any)?.headers,
				`limited_editions_import_template_${today()}.xlsx`,
			);
		} catch {
			notification.error({
				message: 'Download failed',
				description: 'Unable to download the import template.',
				duration: 2,
			});
		} finally {
			setExportingNumbers(false);
		}
	};

	const importNumbers = async (file: File | Blob) => {
		setImportingNumbers(true);
		setImportResult(null);
		try {
			const limitedEditionApi = await LimitedEditionApiClient(instance);
			const response = await limitedEditionApi.importLimitedEditions(file);
			const result = response.data;

			setImportResult(result);

			// Invalid rows are reported one by one instead of failing the whole file, and a number already
			// held by an order keeps its assignment: both have to be said, not just the counters.
			if (result.skipped > 0) {
				notification.warning({
					message: 'Import completed with errors',
					description: `${result.created} created, ${result.updated} updated, ${result.skipped} skipped out of ${result.totalRows} rows. See the report below.`,
					duration: 6,
				});
			} else if (result.warnings?.length) {
				notification.warning({
					message: 'Import completed with warnings',
					description: `${result.created} created, ${result.updated} updated out of ${result.totalRows} rows. ${result.warnings.length} numbers are held by an order and kept their assignment.`,
					duration: 6,
				});
			} else {
				notification.success({
					message: 'Import completed',
					description: `${result.created} created, ${result.updated} updated out of ${result.totalRows} rows.`,
					duration: 3,
				});
			}
		} catch (reason: any) {
			notification.error({
				message: 'Import failed',
				description:
					reason?.response?.data?.title ??
					'Unable to import the limited edition numbers.',
				duration: 4,
			});
		} finally {
			setImportingNumbers(false);
		}
	};

	return {
		handleNumbers,
		numbers,
		numbersTotal,
		numbersLoading,
		saveNumber,
		savingNumber,
		removeNumber,
		removingNumber,
		releaseNumber,
		releasingNumber,
		blockNumber,
		blockingNumber,
		assignNumber,
		assigningNumber,
		previewRelease,
		releaseBySapCode,
		releasingBySapCode,
		exportNumbers,
		exportNumbersTemplate,
		exportingNumbers,
		importNumbers,
		importingNumbers,
		importResult,
		clearImportResult: () => setImportResult(null),
	};
};
